// src/utils/gameUtils.js
import GLOBALS from '../config/globals';

export const translateOuts = (ipOuts, isFinal = false) => {
    const inningOuts = ipOuts % 6;
    const halfInningOuts = inningOuts % 3;
    const inningPart = inningOuts < 3 ? GLOBALS.TOP_OF_INNING : GLOBALS.BOTTOM_OF_INNING;
    const inning = Math.floor(ipOuts / 6) + 1;

    const status = {
        complete: `${inningPart} of ${inning},  ${halfInningOuts} outs.`,
        simple: `${inningPart} of ${inning}`,
        inning,
        outs: halfInningOuts,
        inningHalf: inningPart,
    };

    if (isFinal) {
        status.complete = 'Final';
        status.simple = 'Final';
    }

    return status;
};

export const resultTypes = {
    SO: 'so',
    OO: 'oo',
    BB: 'bb',
    H1: 'h1',
    H2: 'h2',
    H3: 'h3',
    HR: 'hr',
    AB: 'ab',
};
const resultTypeValues = Object.values(resultTypes);
export const isResultTypePlateAppearance = resultType => {
    return resultTypeValues.includes(resultType);
};

export const resultMap = {
    SO: 'Strikeout',
    OO: 'Out',
    BB: 'Walk',
    H1: 'Single',
    H2: 'Double',
    H3: 'Triple',
    HR: 'HOME RUN',
};

export const resultMapSmall = {
    SO: 'K',
    OO: 'Out',
    BB: 'BB',
    H1: '1B',
    H2: '2B',
    H3: '3B',
    HR: 'HR',
};

export const translateEvent = (rawEvent, hitType) => {
    let event = resultMap[(rawEvent || '').toUpperCase()];
    const smallEvent = resultMapSmall[(rawEvent || '').toUpperCase()];
    if (event === 'Out' && hitType) {
        const outLabel = { ground_ball: 'Ground Out', line_drive: 'Line Out', fly_ball: 'Fly Out', bunt: 'Bunt Out' }[hitType];
        if (outLabel) event = outLabel;
    }
    return { event, smallEvent };
};

const pitchRollMap = {
    h9: 'Hits',
    hr9: 'Power',
    bb9: 'Walks',
    k9: 'Strikeouts',
};

const pitchRollMapSmall = {
    h9: 'HS',
    hr9: 'PW',
    bb9: 'BB',
    k9: 'KS',
};

export const translatePitchRoll = roll => {
    if (!roll) return { result: '', smallResult: '' };

    const rollBase = roll.slice(0, -2); // Get the base part (e.g., 'h9', 'hr9')
    const modifier = roll.slice(-1); // Get the last character (either 'l' or 'h')
    const action = modifier === 'l' ? '▼' : '▲';

    const translatedBase = pitchRollMap[rollBase.toLowerCase()];
    const translatedBaseSmall = pitchRollMapSmall[rollBase.toLowerCase()];
    if (!translatedBase) return { result: '', smallResult: '' };

    const result = `${action} ${translatedBase}`;
    const smallResult = `${action}${translatedBaseSmall}`;

    return { result, smallResult };
};

export const findSub = (gameEventID, game) => {
    //loop over game.GamePerticipants.GameLineups
    //if you find an added & removed, then create a sub
    //TODO: if you sup on the first play of the game, it shows all players added in the starting lineup
    const addedPlayers = [];
    const removedPlayers = [];
    game.GameParticipants.forEach(gp => {
        gp.GameLineups.forEach(gl => {
            if (gl.gameEventIDEntered == gameEventID) {
                addedPlayers.push(gl.Card.name);
            }
            if (gl.gameEventIDExited == gameEventID) {
                removedPlayers.push(gl.Card.name);
            }
        });
    });

    if (addedPlayers.length && removedPlayers.length) {
        //only return this if there is BOTH added and removed
        const subAddedText = addedPlayers.map(name => `${name} entered the game. `).join('');
        const subRemovedText = removedPlayers.map(name => `${name} left the game. `).join('');
        return subAddedText + subRemovedText;
    }
    return null;
};

const formatManagedSuffix = event => {
    const result = event.managerActionResult || '';
    const action = (event.managerAction || '').split(':')[0].trim();
    if (result.includes('Safe')) return action === 'steal' ? 'Stolen Base' : 'Runner Advances';
    if (result.includes('Out')) return action === 'steal' ? 'Caught Stealing' : 'Runner Thrown Out';
    return result;
};

export const buildTickerText = (event) => {
    if (!event) return '';

    if (event.resultType) {
        let text = translateEvent(event.resultType, event.hitType).event;
        if (event.gidp) text += ', Double Play';
        return text || '';
    }

    if (event.managerActionResult) {
        const suffix = formatManagedSuffix(event);
        const parent = event.eventMeta?.parentResult;
        if (parent?.resultType) {
            const prefix = translateEvent(parent.resultType, parent.hitType).event;
            return `${prefix}, ${suffix}`;
        }
        return suffix;
    }

    return '';
};

const isMultipleRunners = gameEvent => {
    return !!(
        (gameEvent.firstBaseRunnerLineup && gameEvent.secondBaseRunnerLineup) ||
        (gameEvent.firstBaseRunnerLineup && gameEvent.thirdBaseRunnerLineup) ||
        (gameEvent.secondBaseRunnerLineup && gameEvent.thirdBaseRunnerLineup)
    );
};

const isRunner = gameEvent => {
    return !!(gameEvent.firstBaseRunnerLineup || gameEvent.secondBaseRunnerLineup || gameEvent.thirdBaseRunnerLineup);
};

const isOutfieldHit = hitLocation =>
    ['LF', 'CF', 'RF'].some(pos => (hitLocation || '').endsWith(pos));

/**
 * Build the set of valid advance-runner options for a mid-event modal.
 * Consecutive runners are chained (2nd can't advance unless 3rd clears).
 * Non-consecutive runners are independent (1st can go to empty 2nd regardless of 3rd).
 * Returns { options: string[], text: string[] } parallel arrays.
 */
export const buildAdvanceRunnerOptions = advancingRunners => {
    const baseNames = { 1: '1st', 2: '2nd', 3: '3rd' };
    if (!advancingRunners.length) return { options: [], text: [] };

    // Group into consecutive chains (descending within each)
    const desc = [...advancingRunners].sort((a, b) => b - a);
    const chains = [];
    let currentChain = [desc[0]];
    for (let i = 1; i < desc.length; i++) {
        if (desc[i] === desc[i - 1] - 1) {
            currentChain.push(desc[i]);
        } else {
            chains.push(currentChain);
            currentChain = [desc[i]];
        }
    }
    chains.push(currentChain);

    // Each chain → cumulative options from lead runner inward
    const perChain = chains.map(c => c.map((_, i) => c.slice(0, i + 1)));

    // Cross-product across independent chains (include "skip" per chain), exclude all-empty
    const combine = groups => {
        if (!groups.length) return [[]];
        const [first, ...rest] = groups;
        const restCombos = combine(rest);
        const result = [];
        for (const r of restCombos) result.push([...r]);
        for (const opt of first) {
            for (const r of restCombos) result.push([...opt, ...r]);
        }
        return result;
    };

    const allCombos = combine(perChain)
        .filter(c => c.length > 0)
        .sort((a, b) => b.length - a.length || Math.min(...a) - Math.min(...b));

    const options = [];
    const text = [];
    for (const combo of allCombos) {
        const bases = [...combo].sort((a, b) => a - b);
        options.push(bases.join(','));
        if (bases.length === 1) {
            text.push(`Try to advance runner from ${baseNames[bases[0]]}`);
        } else if (bases.length === advancingRunners.length) {
            text.push(`Try to advance all runners (${bases.map(b => baseNames[b]).join(', ')})`);
        } else {
            text.push(`Try to advance runners from ${bases.map(b => baseNames[b]).join(' & ')}`);
        }
    }
    return { options, text };
};

export const isMidEventAdvanceRunners = (gameEvent, isUserPitching) => {
    if (isUserPitching) return false;
    if (!isOutfieldHit(gameEvent.hitLocation)) return false;
    //console.log(gameEvent.resultType);
    if (GLOBALS.OO == gameEvent.resultType) {
        //console.log(gameEvent.ipOuts % 3, !gameEvent.managerAction, isRunner(gameEvent));
        return gameEvent.ipOuts % 3 && !gameEvent.managerAction && isRunner(gameEvent);
    }
    return (
        GLOBALS.MID_EVENTS.includes(gameEvent.resultType) && !gameEvent.managerAction && isMultipleRunners(gameEvent)
    );
};
